import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { format, addDays } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { createTimeOff } from '@/store/slices/timeOffSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Called after a draft is created so the list can refresh its rows. */
  onSubmitted: () => void;
  /** Prefill start and end with this day (yyyy-MM-dd), e.g. from a schedule day. */
  initialDate?: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Form for an employee to request a span of time off for manager approval. */
const RequestTimeOffModal: React.FC<Props> = ({ visible, onClose, onSubmitted, initialDate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<any>();
  const { isSubmitting } = useSelector((state: RootState) => state.timeOff);

  const [start, setStart] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [end, setEnd] = useState(format(addDays(new Date(), 8), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Opening for a specific day starts the request on that day.
  useEffect(() => {
    if (visible && initialDate) {
      setStart(initialDate);
      setEnd(initialDate);
    }
  }, [visible, initialDate]);

  const reset = () => {
    setStart(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
    setEnd(format(addDays(new Date(), 8), 'yyyy-MM-dd'));
    setNotes('');
    setFormError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!DATE_RE.test(start) || !DATE_RE.test(end)) {
      setFormError('Dates must be in YYYY-MM-DD format.');
      return;
    }
    if (end < start) {
      setFormError('End date must be on or after the start date.');
      return;
    }
    setFormError(null);
    const result = await dispatch(
      createTimeOff({ start, end, notes: notes.trim() || undefined, draft: true })
    );
    if (createTimeOff.fulfilled.match(result)) {
      if (result.payload.success && result.payload.request) {
        // Draft created — refresh the list, then jump straight into assembling
        // coverage for the new request rather than dismissing back to the list.
        const requestId = result.payload.request.id;
        onSubmitted();
        close();
        navigation.navigate('TimeOffAssemble', { requestId });
      } else {
        setFormError(result.payload.message || 'Request could not be created.');
      }
    } else {
      setFormError((result.payload as string) || 'Request could not be created.');
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={close}>
      <KeyboardAvoidingView
        style={styles.backdrop}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <Text style={styles.title}>REQUEST TIME OFF</Text>
            <TouchableOpacity
              onPress={close}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>
          <Text style={styles.subtitle}>
            Pick the days you'll be away. Next: choose who covers your shifts, then submit.
          </Text>

          <ScrollView keyboardShouldPersistTaps="handled" style={styles.form}>
            <View style={styles.dateRow}>
              <View style={styles.dateCol}>
                <Text style={styles.label}>FIRST DAY</Text>
                <TextInput
                  style={styles.input}
                  value={start}
                  onChangeText={setStart}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={colors.inkFaint}
                  autoCapitalize="none"
                />
              </View>
              <View style={styles.dateCol}>
                <Text style={styles.label}>LAST DAY</Text>
                <TextInput
                  style={styles.input}
                  value={end}
                  onChangeText={setEnd}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={colors.inkFaint}
                  autoCapitalize="none"
                />
              </View>
            </View>

            <Text style={styles.label}>NOTE FOR YOUR MANAGER</Text>
            <TextInput
              style={[styles.input, styles.inputMultiline]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Optional — reason or context"
              placeholderTextColor={colors.inkFaint}
              multiline
            />

            {formError && (
              <View style={styles.errorBox}>
                <Icon name="alert-circle" size={16} color={colors.accentDark} />
                <Text style={styles.errorText}>{formError}</Text>
              </View>
            )}
          </ScrollView>

          <TouchableOpacity
            style={[styles.submit, isSubmitting && styles.submitBusy]}
            activeOpacity={0.85}
            disabled={isSubmitting}
            onPress={submit}>
            <Text style={styles.submitText}>{isSubmitting ? 'CREATING…' : 'NEXT: PICK COVERAGE'}</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
    maxHeight: '90%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: font.display,
    fontSize: 28,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontFamily: font.body,
    fontSize: 13.5,
    lineHeight: 19,
    color: colors.inkSoft,
    marginTop: 2,
    marginBottom: space.md,
  },
  form: {
    flexGrow: 0,
  },
  label: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.6,
    color: colors.inkFaint,
    marginBottom: space.xs,
    marginTop: space.md,
  },
  input: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.ink,
  },
  inputMultiline: {
    minHeight: 84,
    textAlignVertical: 'top',
  },
  dateRow: {
    flexDirection: 'row',
    gap: space.md,
  },
  dateCol: {
    flex: 1,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: radius.sm,
    padding: space.sm,
    marginTop: space.md,
  },
  errorText: {
    flex: 1,
    marginLeft: space.xs,
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
  },
  submit: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: space.lg,
  },
  submitBusy: {
    opacity: 0.5,
  },
  submitText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default RequestTimeOffModal;
